import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
  Typography,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
} from '@mui/material';
import { Clear, TuneRounded } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';

import ErrorMsg from '../common/ErrorMsg';
import errorResponses from '../../errorResponses';
import useGlynt from '../hooks/useGlynt';
import usePaginatedExtractionBatches from '../hooks/usePaginatedExtractionBatches';
import useSearchParams from '../hooks/useSearchParams';
import DocumentTableBulkStatusUpdate from '../documents/DocumentTableBulkStatusUpdate';
import DocumentTableCreateDraftPackage from '../documents/DocumentTableCreateDraftPackage';
import { getIdFromUrl } from '../../utils/getIdFromUrl';
import { AppContext } from '../../App';
import { IExtractionBatch } from '../../types/my-types';
import { CORE_VERSION, EB_LIMIT } from '../../constants';
import { UploadContextProvider } from '../upload/UploadContext';
import ExtractionBatchNew from './create/ExtractionBatchNew';
import ExtractionBatchList from './ExtractionBatchList';
import ExtractionBatchFiltersPopover, {
  EBFilterValues,
  EB_FILTER_KEYS,
} from './ExtractionBatchFiltersPopover';
import { filterInputSx, dropdownChipSx, activeFilterPillSx } from '../common/filterStyles';
import { useDebouncedSearchParam } from '../hooks/useDebouncedSearchParam';
import RefreshButton from '../common/Buttons/RefreshButton';

/* ── Style constants ── */

const FILTER_LABELS: Record<string, string> = {
  eb_id: 'EB ID',
  document_id: 'Document',
  verified: 'Sent',
  extraction_id: 'Extraction',
  training_set_label: 'Training Set',
  created_at: 'Created',
  validation_errors: 'Errors',
  training_set_certified: 'Certified',
};

function ExtractionBatches() {
  const { dataPoolId } = React.useContext(AppContext);
  const glynt = useGlynt();

  const { search } = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(search);

  const perPage = Number(searchParams.get('perPage')) || EB_LIMIT;
  const page = Number(searchParams.get('page')) || 0;
  const labelContains = searchParams.get('label__contains') || '';
  const ebId = searchParams.get('eb_id') || '';
  const documentId = searchParams.get('document_id') || '';
  const verified = searchParams.get('verified') || '';
  const extractionId = searchParams.get('extraction_id') || '';
  const trainingSetLabel = searchParams.get('training_set_label') || '';
  const createdAt = searchParams.get('created_at') || '';
  const hasUndismissedValidationErrors = searchParams.get('validation_errors') || '';
  const trainingSetCertified = searchParams.get('training_set_certified') || '';

  const { setSearchParam, removeSearchParamSingle } = useSearchParams();

  const {
    data: dataPool,
    isError: errorDataPool,
  } = useQuery({
    queryKey: ['dataPool', dataPoolId],
    queryFn: async () => await (await glynt).dataPool.retrieve(),
    enabled: !!dataPoolId,
  });

  const { data, isPending, isFetching, isError, refetch } = usePaginatedExtractionBatches({
    limit: perPage,
    defaultPage: page,
    labelContains,
    ebId,
    documentId,
    verified,
    extractionId,
    trainingSetLabel,
    createdAt,
    hasUndismissedValidationErrors,
    trainingSetCertified,
  });

  if (isError || errorDataPool) {
    return <ErrorMsg error={errorResponses.failedToLoadExtractionBatches} />;
  }

  const extractionBatches: IExtractionBatch[] = data?.results || [];
  const count: number = data?.count || 0;

  /* ── Debounced label input ── */
  const { localValue: localLabel, setLocalValue: setLocalLabel, debouncedSet: debouncedSetLabel, clear: clearLabel } = useDebouncedSearchParam('label__contains', labelContains);

  /* ── EB ID input (applies on Enter/blur) ── */
  const [localEbId, setLocalEbId] = useState(ebId);
  useEffect(() => setLocalEbId(ebId), [ebId]);
  const applyEbId = () => {
    if (localEbId.trim()) {
      setSearchParam('eb_id', localEbId.trim());
    } else {
      removeSearchParamSingle('eb_id');
    }
  };
  const clearEbId = () => {
    setLocalEbId('');
    removeSearchParamSingle('eb_id');
  };

  /* ── Filters popover ── */
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<null | HTMLElement>(null);

  const currentFilterValues: EBFilterValues = {
    document_id: documentId,
    verified,
    extraction_id: extractionId,
    training_set_label: trainingSetLabel,
    created_at: createdAt,
    validation_errors: hasUndismissedValidationErrors,
    training_set_certified: trainingSetCertified,
  };

  const handleApplyFilters = (values: EBFilterValues) => {
    const params = new URLSearchParams(search);
    params.set('page', '0');
    EB_FILTER_KEYS.forEach((key) => {
      const val = values[key];
      if (val) {
        params.set(key, val);
      } else {
        params.delete(key);
      }
    });
    navigate({ search: params.toString() });
    setPopoverAnchorEl(null);
  };

  const popoverFiltersCount = EB_FILTER_KEYS.filter((k) => searchParams.get(k)).length;
  const totalActiveFilters = popoverFiltersCount + (labelContains ? 1 : 0) + (ebId ? 1 : 0);

  const selectedEbIds = searchParams.get('selectedIds')?.split(',') || [];

  const resolveDocumentIdsForSelectedEbs = async (): Promise<string[]> => {
    if (selectedEbIds.length === 0) return [];
    const client = await glynt;
    const batches = await Promise.all(
      selectedEbIds.map((id) => client.extractionBatch.retrieve(id))
    );
    const ids = new Set<string>();
    for (const batch of batches) {
      for (const docUrl of batch.documents || []) {
        const id = getIdFromUrl(docUrl);
        if (id) ids.add(id);
      }
    }
    return Array.from(ids);
  };

  return (
    <Box sx={{ backgroundColor: '#F5F6FA', padding: '20px 40px' }}>
      {/* ── Row 1: Title bar ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Extraction Batches
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: '#65BBA9', fontWeight: 600 }}
          >
            {isPending || isFetching ? '...' : (
              <>(<span data-cy="span-eb-count">{count.toLocaleString()}</span>)</>
            )}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {selectedEbIds.length > 0 ? (
            <>
              <DocumentTableBulkStatusUpdate
                documents={[]}
                resolveSelectedDocumentIds={resolveDocumentIdsForSelectedEbs}
                resolvingLabel={`Loading documents for ${selectedEbIds.length} extraction batch${
                  selectedEbIds.length !== 1 ? 'es' : ''
                }…`}
                onSuccess={() => {
                  refetch();
                }}
              />
              <DocumentTableCreateDraftPackage mode="extraction_batches" />
            </>
          ) : null}
          <UploadContextProvider>
            <ExtractionBatchNew extractionBatches={extractionBatches} coreVersion={CORE_VERSION} />
          </UploadContextProvider>
        </Box>
      </Box>

      {/* ── Row 2: Filter controls — right-aligned ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: totalActiveFilters > 0 ? 1 : 1.5,
          justifyContent: 'flex-end',
        }}
      >
        <TextField
          size="small"
          placeholder="EB ID..."
          variant="outlined"
          autoComplete="off"
          value={localEbId}
          onChange={(e) => setLocalEbId(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && applyEbId()}
          onBlur={applyEbId}
          data-cy="filter-eb-id"
          sx={{ width: 120, ...filterInputSx }}
          InputProps={{
            endAdornment: localEbId && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={clearEbId}
                  edge="end"
                  sx={{ mr: -1, p: 0.25 }}
                >
                  <Clear sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          size="small"
          placeholder="Search by label..."
          variant="outlined"
          autoComplete="off"
          value={localLabel}
          onChange={(e) => {
            setLocalLabel(e.target.value);
            debouncedSetLabel(e.target.value);
          }}
          data-cy="text-eb-search-input"
          sx={{ width: 180, ...filterInputSx }}
          InputProps={{
            endAdornment: localLabel && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => clearLabel()}
                  edge="end"
                  sx={{ mr: -1, p: 0.25 }}
                >
                  <Clear sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Chip
          icon={<TuneRounded sx={{ fontSize: '16px !important' }} />}
          label={popoverFiltersCount > 0 ? `Filters (${popoverFiltersCount})` : 'Filters'}
          onClick={(e) => setPopoverAnchorEl(e.currentTarget)}
          data-cy="btn-more-filters"
          sx={{
            ...dropdownChipSx,
            ...(popoverFiltersCount > 0 && {
              borderColor: '#1D9AAA',
              backgroundColor: 'rgba(29,154,170,0.06)',
            }),
          }}
        />

        <ExtractionBatchFiltersPopover
          anchorEl={popoverAnchorEl}
          currentValues={currentFilterValues}
          onClose={() => setPopoverAnchorEl(null)}
          onApply={handleApplyFilters}
        />

        <RefreshButton
          cypressId="button-refresh"
          disabled={isPending || isFetching}
          isLoading={isPending || isFetching}
          onClick={() => refetch()}
        />
      </Box>

      {/* ── Row 3: Active filter pills — right-aligned ── */}
      {totalActiveFilters > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {/* EB ID pill */}
          {ebId && (
            <Chip
              label={`EB ID: ${ebId}`}
              onDelete={() => clearEbId()}
              data-cy="pill-eb_id"
              sx={activeFilterPillSx}
            />
          )}
          {labelContains && (
            <Chip
              label={`Label: ${labelContains}`}
              onDelete={() => clearLabel()}
              data-cy={`chip-docfilter-EB Label-${labelContains}`}
              sx={activeFilterPillSx}
            />
          )}
          {EB_FILTER_KEYS.map((key) => {
            const value = searchParams.get(key);
            if (!value) return null;
            return (
              <Chip
                key={key}
                label={`${FILTER_LABELS[key]}: ${value}`}
                onDelete={() => removeSearchParamSingle(key)}
                data-cy={`pill-${key}`}
                sx={activeFilterPillSx}
              />
            );
          })}
        </Box>
      )}

      {/* ── Table ── */}
      <ExtractionBatchList
        extractionBatches={extractionBatches}
        count={count}
        page={page}
        perPage={perPage}
        refetch={refetch}
        nextPage={() => {
          const total = data ? Math.ceil(data.count / perPage) : 1;
          if (page < total - 1) setSearchParam('page', page + 1);
        }}
        previousPage={() => {
          if (page > 0) setSearchParam('page', page - 1);
        }}
        isPending={isPending}
        isFetching={isFetching}
      />
    </Box>
  );
}

export default ExtractionBatches;
